import { GetMainMenuItemsParams, MenuItemDef } from "ag-grid-enterprise";
import { global } from "@shared/libs/Global";
import type { ExtraHeaderMenuItem } from "./SnGridTypes";
import { callApi } from "@shared/api/restApi";
import { showDialog } from "@widgets/CommonDialog";
// [보류] 항목 복원의 SYSTEM 권한 확인을 되살릴 때 함께 주석 해제한다.
// import { confirmSystemPassword } from "@widgets/commonpopup/sys/confirmSystemPassword";

export function createMainMenuItems(
  params: GetMainMenuItemsParams,
  options: {
    columnSave: () => void;
    headerChange: () => void;
    columnInit: () => void;
    filterBoxRef: React.RefObject<{ openFilterBox: () => void }>;
    enableRowDrag: () => void;
    props: any;
    extraHeaderMenuItems?: ExtraHeaderMenuItem[];
    onExtraHeaderMenuClick?: (event: { id: string; params: any }) => void;
  }
): (MenuItemDef | string)[] {
  const {
    columnSave,
    headerChange,
    columnInit,
    filterBoxRef,
    enableRowDrag,
    props,
    extraHeaderMenuItems,
    onExtraHeaderMenuClick,
  } = options;
  // 항목 복원은 모든 사용자/모든 컬럼에서 노출한다.
  // ag-grid 기본 "columnChooser"는 클릭 즉시 열려서 권한 확인을 끼워넣을 수 없어
  // 동일한 이름/아이콘의 커스텀 항목으로 대체한다.
  //
  // [보류] SYSTEM 권한 확인은 요청에 따라 잠시 꺼 둔다(나중에 되살릴 예정).
  // 되살릴 때는 아래 action 본문과 파일 상단의 confirmSystemPassword import를
  // 함께 주석 해제하면 된다.
  const columnChooserItem: MenuItemDef = {
    name: "항목 복원",
    icon: '<span class="ag-icon ag-icon-columns" role="presentation" unselectable="on"></span>',
    action: async () => {
      // if (await confirmSystemPassword()) {
      //   params.api.showColumnChooser();
      // }
      params.api.showColumnChooser();
    },
  };

  // const columnId = null;
  const columnId = params.column?.getColId();
  if (columnId === "fixIndicator") {
    // No. 컬럼은 기본 메뉴를 그대로 쓰되, 항목 복원만 커스텀 항목으로 교체한다.
    return (params.defaultItems ?? []).map((item) =>
      item === "columnChooser" ? columnChooserItem : item
    );
  }

  // 항목설정저장 위에 사용자 추가 메뉴를 먼저 배치합니다.
  const prependExtraItems: (MenuItemDef | string)[] = (
    extraHeaderMenuItems ?? []
  )
    .filter((item) => item?.visible !== false)
    .map((item) => ({
      name: item.name,
      action: () => {
        // 실제 기능은 화면에서 id 기준으로 분기 처리
        onExtraHeaderMenuClick?.({ id: item.id, params });
      },
    }));

  // 항목설정저장/초기화는 sys_formgridcolumn 기준이라, 다른 곳에 컬럼 정의를 둔 그리드는 숨긴다.
  const allowColumnSetting = props?.etcProps?.disableColumnSetting !== true;
  const columnSaveItems: MenuItemDef[] = allowColumnSetting
    ? [
        {
          name: "항목설정저장",
          action: columnSave,
        },
      ]
    : [];

  const athleteMenuItems: (MenuItemDef | string)[] = [
    ...prependExtraItems,
    ...(prependExtraItems.length > 0 ? ["separator"] : []),
    ...(global.getState().userInfo.cd_user === "SYSTEM"
      ? [
          ...columnSaveItems,
          {
            name: "항목명변경",
            action: headerChange,
          },
          {
            name: "항목 제거",
            action: () => {
              const columnId = params.column?.getColId();
              if (columnId) {
                params.api.setColumnsVisible([columnId], false);
              }
            },
          },
        ]
      : columnSaveItems),
  ];

  // 아래 tail 필터는 인덱스 기준이라 컬럼마다 자르는 항목이 달라진다.
  // 그래서 항목 복원은 여기서 명시적으로 넣고 tail에서는 이름으로 항상 제외한다.
  athleteMenuItems.push(columnChooserItem);
  if (allowColumnSetting) {
    athleteMenuItems.push({
      name: "항목 초기화",
      action: columnInit,
    });
  }

  athleteMenuItems.push(
    ...params.defaultItems.filter(
      (item, index) =>
        item !== "columnChooser" && ![0, 1, 9, 10, 11].includes(index)
    )
  );

  athleteMenuItems.push(
    "separator",
    {
      name: "필터/ 필터해제",
      action: () => {
        const headerIcons = document.querySelectorAll(
          ".ag-header-cell .ag-header-icon"
        );
        headerIcons.forEach((icon) => {
          icon.classList.toggle("hidden-filter");
        });
      },
    },
    {
      name: "찾기",
      action: () => {
        filterBoxRef.current?.openFilterBox();
      },
    },
    {
      name: "순서변경",
      action: () => {
        setTimeout(() => {
          enableRowDrag();
        }, 100);
      },
    }
  );
  return athleteMenuItems;
}

export async function FN_Fi_AutoSlip(
  figure: "Y" | "N",
  cd_menu: string,
  rowNodes: any[]
) {
  const config = global.getState().config;
  const dc_ctrl = config.MAS0031?.dc_ctrl;
  if (!dc_ctrl || dc_ctrl === "") {
    showDialog("alter", "회계 연동 시스템 설정을 확인해 주세요.");
    return;
  }

  const rows = rowNodes.map((rowNode) => rowNode.data);

  // 회계처리 여부 값 검증
  if (rows.some((row) => row.yn_docu !== "Y" && row.yn_docu !== "N")) {
    showDialog("alter", "회계처리 여부를 알 수 없는 대상이 존재합니다.");
    return;
  }

  // 회계처리 검증
  if (figure === "Y" && rows.some((row) => row.yn_docu === "Y")) {
    showDialog("alter", "이미 처리된 대상이 존재합니다.");
    return;
  }

  // 회계처리 취소 검증
  if (figure === "N" && rows.some((row) => row.yn_docu === "N")) {
    showDialog("alter", "회계전표 처리가 완료되지 않은 건입니다.");
    return;
  }

  const endpoint = figure === "Y" ? "autoslip/create" : "autoslip/cancel";
  const result = await callApi(endpoint, { cd_menu, rows });
  if (!result.status) {
    return;
  }

  rowNodes.forEach((rowNode: any) => {
    rowNode.setDataValue("yn_docu", figure, "noChange");
  });
}
